import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSearchParams } from 'react-router-dom';

import { getCameras, createCamera, updateCamera, deleteCamera } from '../../services/cameraService';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All cameras' },
  { value: 'online', label: 'Online only' },
  { value: 'offline', label: 'Offline only' },
];

const emptyForm = {
  name: '',
  location: '',
  ip_address: '',
  rtsp_path: '',
  username: '',
  password: '',
  port: 554,
  is_active: true,
};

const CamerasPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';

  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingCamera, setEditingCamera] = useState(null);
  const [statusFilter, setStatusFilter] = useState(
    STATUS_FILTER_OPTIONS.some((option) => option.value === initialStatus) ? initialStatus : 'all',
  );

  useEffect(() => {
    const loadCameras = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getCameras();
        setCameras(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Unable to load cameras.');
      } finally {
        setLoading(false);
      }
    };

    loadCameras();
  }, []);

  useEffect(() => {
    const params = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    setSearchParams(params, { replace: true });
  }, [statusFilter, setSearchParams]);

  const filteredCameras = useMemo(() => {
    if (statusFilter === 'all') {
      return cameras;
    }
    if (statusFilter === 'online') {
      return cameras.filter((camera) => camera.is_active !== false);
    }
    return cameras.filter((camera) => camera.is_active === false);
  }, [cameras, statusFilter]);

  const openDialog = (camera = null) => {
    if (camera) {
      setEditingCamera(camera);
      setForm({
        name: camera.name || '',
        location: camera.location || '',
        ip_address: camera.ip_address || '',
        rtsp_path: camera.rtsp_path || '',
        username: camera.username || '',
        password: '',
        port: camera.port || 554,
        is_active: camera.is_active,
      });
    } else {
      setEditingCamera(null);
      setForm(emptyForm);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(emptyForm);
    setEditingCamera(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (event) => {
    setForm((prev) => ({ ...prev, is_active: event.target.checked }));
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      port: Number(form.port) || 554,
    };
    if (!payload.name || !payload.ip_address || !payload.rtsp_path) {
      setError('Name, IP address, and RTSP path are required.');
      return;
    }

    if (editingCamera && !payload.password) {
      delete payload.password;
    }

    try {
      if (editingCamera) {
        await updateCamera(editingCamera.id, payload);
      } else {
        await createCamera(payload);
      }
      closeDialog();
      const refreshed = await getCameras();
      setCameras(Array.isArray(refreshed) ? refreshed : []);
    } catch (err) {
      setError('Unable to save camera. Please verify the connection details.');
    }
  };

  const handleDelete = async (camera) => {
    const confirmed = window.confirm(`Delete camera "${camera.name}"?`);
    if (!confirmed) return;
    try {
      await deleteCamera(camera.id);
      const refreshed = await getCameras();
      setCameras(Array.isArray(refreshed) ? refreshed : []);
    } catch (err) {
      setError('Unable to delete camera.');
    }
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Cameras</Typography>
        <Button variant="contained" color="primary" onClick={() => openDialog()}>
          Add Camera
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            mb={3}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                displayEmpty
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => setStatusFilter('all')}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
            >
              Reset filters
            </Button>
          </Stack>

          {filteredCameras.length === 0 ? (
            <Typography color="text.secondary">No cameras match the selected filters.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>IP Address</TableCell>
                <TableCell>RTSP Path</TableCell>
                <TableCell>Heartbeat</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCameras.map((camera) => (
                    <TableRow key={camera.id}>
                      <TableCell>{camera.name}</TableCell>
                  <TableCell>{camera.location || 'N/A'}</TableCell>
                  <TableCell>{camera.ip_address}</TableCell>
                  <TableCell>{camera.rtsp_path}</TableCell>
                  <TableCell>
                    {camera.last_health_check
                      ? new Date(camera.last_health_check).toLocaleString()
                      : 'No heartbeat yet'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={camera.is_active !== false ? 'Online' : 'Offline'}
                      color={camera.is_active !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => openDialog(camera)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(camera)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingCamera ? 'Edit Camera' : 'Add Camera'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
            <TextField label="Location" name="location" value={form.location} onChange={handleChange} fullWidth />
            <TextField label="IP Address" name="ip_address" value={form.ip_address} onChange={handleChange} required fullWidth />
            <TextField label="RTSP Path" name="rtsp_path" value={form.rtsp_path} onChange={handleChange} required fullWidth helperText="Example: /stream1" />
            <TextField label="Username" name="username" value={form.username} onChange={handleChange} fullWidth />
            <TextField label="Password" name="password" type="password" value={form.password} onChange={handleChange} fullWidth helperText={editingCamera ? 'Leave blank to keep current password' : ''} />
            <TextField label="Port" name="port" type="number" value={form.port} onChange={handleChange} fullWidth />
            <FormControlLabel control={<Switch checked={form.is_active} onChange={handleToggle} />} label="Camera active" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingCamera ? 'Save Changes' : 'Create Camera'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CamerasPage;
